const API_BASE_URL = (process.env.REACT_APP_BASEURL || "").trim().replace(/\/$/, "");
const ENDPOINT = `${API_BASE_URL}/api/activity/events`;
const MAX_BATCH = 25;
const FLUSH_MS = 5000;
const sessionKey = "aptance_activity_session";

const randomId = () =>
  window.crypto?.randomUUID?.().replaceAll("-", "") ||
  `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

const clean = (value, max = 100) =>
  Array.from(String(value || "unknown"))
    .filter((character) => character.charCodeAt(0) >= 32 && character.charCodeAt(0) !== 127)
    .join("").trim().slice(0, max);

const moduleFromPath = (path = window.location.pathname) => {
  const parts = path.split("/").filter(Boolean);
  const value = parts.find((part) => !/^[0-9a-f-]{20,}$/i.test(part)) || "application";
  return clean(value, 40).toLowerCase();
};

let queue = [];
let timer;
let initialized = false;
let sending = false;

export const getActivitySessionId = () => {
  let id = sessionStorage.getItem(sessionKey);
  if (!id) { id = randomId(); sessionStorage.setItem(sessionKey, id); }
  return id;
};

export const newCorrelationId = () => randomId();

const authenticated = () => localStorage.getItem("sessionActive") === "true";

export const trackActivity = (eventType, action, options = {}) => {
  if (!authenticated() && !options.allowUnauthenticated) return;
  queue.push({
    eventId: randomId(),
    sessionId: getActivitySessionId(),
    correlationId: options.correlationId,
    timestamp: new Date().toISOString(),
    module: clean(options.module || moduleFromPath(), 40),
    eventType: clean(eventType, 40),
    action: clean(action, 100),
    status: ["failed", "cancelled"].includes(options.status) ? options.status : "success",
    resourceId: options.resourceId ? clean(options.resourceId, 100) : undefined,
    metadata: Object.fromEntries(Object.entries(options.metadata || {}).slice(0, 10)
      .map(([key, value]) => [clean(key, 40), clean(value, 200)])),
  });
  if (queue.length >= MAX_BATCH) void flushActivities();
};

export const flushActivities = async () => {
  if (sending || queue.length === 0 || !authenticated()) return;
  sending = true;
  const events = queue.splice(0, MAX_BATCH);
  try {
    const response = await window.fetch(ENDPOINT, {
      method: "POST", credentials: "include", keepalive: true,
      headers: { "Content-Type": "application/json" }, body: JSON.stringify({ events }),
    });
    if (!response.ok && response.status !== 401 && response.status !== 403 && response.status !== 429)
      queue = [...events, ...queue].slice(0, 100);
  } catch { queue = [...events, ...queue].slice(0, 100); }
  finally { sending = false; }
};

export const startAuthenticatedTracking = () => {
  getActivitySessionId();
  trackActivity("page_visit", "login_completed", { module: "authentication" });
  void flushActivities();
};

export const endActivitySession = () => sessionStorage.removeItem(sessionKey);

const labelFor = (element) => clean(
  element.dataset.trackAction || element.getAttribute("aria-label") || element.title || element.textContent,
  80
);

const handleClick = (event) => {
  const target = event.target.closest("button, a, [role='button'], [role='tab'], [data-track-action]");
  if (!target || target.dataset.trackIgnore === "true") return;
  const label = labelFor(target);
  if (!label || label === "unknown") return;
  const href = target.getAttribute("href") || "";
  const isDownload = target.hasAttribute("download") || /\.(pdf|xlsx?|csv|docx?)($|\?)/i.test(href);
  const isTab = target.getAttribute("role") === "tab" || target.dataset.bsToggle === "tab";
  const isModal = target.dataset.bsToggle === "modal";
  trackActivity(isDownload ? "document_download" : isTab ? "tab" : isModal ? "modal" : "button_click",
    label, { metadata: { label, control: target.tagName.toLowerCase() } });
};

const handleSubmit = (event) => {
  const form = event.target;
  const label = clean(form.dataset.trackAction || form.getAttribute("aria-label") || form.id || form.name || "form_submit");
  const hasSearch = Boolean(form.querySelector("input[type='search'], input[name*='search' i]"));
  trackActivity(hasSearch ? "search" : "form_submit", label, { metadata: { route: window.location.pathname } });
};

const handleInvalid = (event) => {
  const form = event.target.form;
  trackActivity("validation_failure", clean(form?.dataset.trackAction || form?.id || "form_validation"),
    { status: "failed", metadata: { control: clean(event.target.name || event.target.id || event.target.type, 40) } });
};

const handleChange = (event) => {
  const target = event.target;
  if (target.type === "file" && target.files?.length) {
    const extension = target.files[0].name.split(".").pop()?.toLowerCase() || "unknown";
    trackActivity("document_upload", "file_selected", { metadata: { file_type: extension } });
  } else if (target.tagName === "SELECT" && (target.dataset.trackFilter || /filter|country|status|type/i.test(target.name || target.id))) {
    trackActivity("filter", clean(target.dataset.trackFilter || target.name || target.id || "filter_applied"),
      { metadata: { filter_count: target.value ? "1" : "0" } });
  }
};

export const initializeActivityTracking = (router) => {
  if (initialized) return () => {};
  initialized = true;
  let lastPath = window.location.pathname + window.location.search;
  const unsubscribe = router.subscribe((state) => {
    const nextPath = state.location.pathname + state.location.search;
    if (nextPath !== lastPath && authenticated()) {
      lastPath = nextPath;
      trackActivity("page_visit", "route_navigation", { module: moduleFromPath(state.location.pathname), metadata: { route: state.location.pathname } });
    }
  });
  document.addEventListener("click", handleClick, true);
  document.addEventListener("submit", handleSubmit, true);
  document.addEventListener("invalid", handleInvalid, true);
  document.addEventListener("change", handleChange, true);
  window.addEventListener("error", () => trackActivity("frontend_error", "window_error", { status: "failed", metadata: { error_type: "runtime" } }));
  window.addEventListener("unhandledrejection", () => trackActivity("frontend_error", "unhandled_promise", { status: "failed", metadata: { error_type: "promise" } }));
  timer = window.setInterval(() => void flushActivities(), FLUSH_MS);
  if (authenticated()) trackActivity("page_visit", "application_loaded", { metadata: { route: window.location.pathname } });
  return () => { unsubscribe(); window.clearInterval(timer); };
};
